/**
 * Phase 11 Beat 3 — Portal Notifications list. Bell icon + panel.
 */
import { useCallback, useEffect, useState } from "react";
import { Bell, Check } from "@phosphor-icons/react";

const KIND_TONE = {
  change_request_submitted: "sky",
  change_request_approved: "emerald",
  change_request_rejected: "red",
  change_request_clarification: "amber",
  evaluation_completed: "emerald",
  marketing_ready: "indigo",
};

export default function PortalNotifications({ authFetch, unreadHint = 0, onRefresh }) {
  const [rows, setRows] = useState([]);
  const [unread, setUnread] = useState(unreadHint);

  const load = useCallback(async () => {
    try {
      const r = await authFetch("/api/portal/notifications");
      const d = await r.json();
      setRows(d.rows || []); setUnread(d.unread || 0);
    } catch { /* silent */ }
  }, [authFetch]);
  useEffect(() => { load(); }, [load]);

  const markRead = async (nid) => {
    await authFetch(`/api/portal/notifications/${nid}/read`, { method: "POST" });
    load(); onRefresh && onRefresh();
  };
  const markAllRead = async () => {
    await authFetch("/api/portal/notifications/read-all", { method: "POST" });
    load(); onRefresh && onRefresh();
  };

  return (
    <div className="space-y-3" data-testid="portal-notifications">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold text-slate-900 inline-flex items-center gap-2">
          <Bell size={18} weight="fill" className="text-sky-600" /> Notifications
          {unread > 0 && (
            <span className="bg-sky-600 text-white rounded-full text-xs px-2 py-0.5 font-semibold" data-testid="notif-unread-badge">{unread}</span>
          )}
        </h3>
        {unread > 0 && (
          <button onClick={markAllRead} data-testid="notif-mark-all-read"
                  className="text-xs text-slate-500 hover:text-sky-700 inline-flex items-center gap-1 font-medium">
            <Check size={12} /> Mark all as read
          </button>
        )}
      </div>

      {rows.length === 0 && (
        <div className="bg-white border border-slate-200 rounded-2xl p-6 text-center text-sm text-slate-500">
          You're all caught up! No notifications yet.
        </div>
      )}

      <ul className="space-y-2">
        {rows.map((n) => {
          const tone = KIND_TONE[n.kind] || "slate";
          const toneMap = {
            slate: "bg-slate-50 border-slate-200",
            sky: "bg-sky-50 border-sky-200",
            emerald: "bg-emerald-50 border-emerald-200",
            red: "bg-red-50 border-red-200",
            amber: "bg-amber-50 border-amber-200",
            indigo: "bg-indigo-50 border-indigo-200",
          };
          return (
            <li key={n.id} className={`rounded-xl border p-3 ${toneMap[tone]} ${n.read_at ? "opacity-60" : ""}`}
                data-testid={`notif-item-${n.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-900">{n.title}</div>
                  <div className="text-xs text-slate-600 mt-0.5 whitespace-pre-line">{n.body}</div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-400 mt-1">{(n.created_at || "").slice(0, 16).replace("T", " ")}</div>
                </div>
                {!n.read_at && (
                  <button onClick={() => markRead(n.id)} data-testid={`notif-mark-read-${n.id}`}
                          className="text-xs text-slate-500 hover:text-sky-700 shrink-0">Mark read</button>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
